import React, { useState } from "react";
import { Button, Checkbox, Input, Tag, Empty, Popconfirm, Tooltip } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, CheckOutlined, ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { ButtonItem } from "../../../../Components/Items";

// Captura de la rutina dentro del servicio (controlado: value / onChange).
// value: { rutinaId, codigo, titulo, secciones: [{ titulo, tipo: "checklist", actividades }] }
// Es una COPIA de la rutina del catalogo: con "Editar pasos" se agregan, quitan o reordenan
// pasos y secciones sólo para este servicio (la rutina original no cambia).

// Avance de la rutina (se usa tambien en el paso del servicio): { hechas, total, noAplica }.
// total = pasos que aplican; los marcados "No aplica" se cuentan aparte.
export const avanceRutina = (rutina) => {
    let hechas = 0;
    let total = 0;
    let noAplica = 0;
    (rutina?.secciones || []).forEach((s) =>
        (s.actividades || []).forEach((a) => {
            if (a.noAplica) return noAplica++;
            total++;
            if (a.realizado) hechas++;
        })
    );
    return { hechas, total, noAplica };
};

const pasoNuevo = () => ({ titulo: "", descripcion: "", realizado: false, noAplica: false, observacion: "" });

const rutinaVacia = () => ({
    rutinaId: null,
    codigo: "",
    titulo: "Rutina del servicio",
    secciones: [{ titulo: "Rutina de mantenimiento", tipo: "checklist", actividades: [pasoNuevo()], observacionesGrupo: [] }],
});

const mover = (arr, i, d) => {
    const j = i + d;
    if (j < 0 || j >= arr.length) return arr;
    const copia = [...arr];
    [copia[i], copia[j]] = [copia[j], copia[i]];
    return copia;
};

const RutinaServicio = ({ value, onChange, disabled }) => {
    const [editando, setEditando] = useState(false);

    if (!value || !(value.secciones || []).length)
        return (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Este servicio no tiene rutina. Elija una de las ya creadas arriba, o arme una sólo para este servicio.">
                {!disabled && (
                    <ButtonItem
                        type="dashed"
                        htmlType="button"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            onChange(rutinaVacia());
                            setEditando(true);
                        }}
                    >
                        Armar rutina para este servicio
                    </ButtonItem>
                )}
            </Empty>
        );

    const enEdicion = editando && !disabled;
    const setSecciones = (secciones) => onChange({ ...value, secciones });
    const setSeccion = (i, cambios) => setSecciones(value.secciones.map((s, j) => (j === i ? { ...s, ...cambios } : s)));
    const setPasos = (i, actividades) => setSeccion(i, { actividades });
    const cambiarPaso = (i, k, cambios) => setPasos(i, value.secciones[i].actividades.map((a, n) => (n === k ? { ...a, ...cambios } : a)));
    // Los que no aplican se quedan asi.
    const marcarTodo = (i, realizado) => setPasos(i, value.secciones[i].actividades.map((a) => ({ ...a, realizado: !a.noAplica && realizado })));

    return (
        <div>
            {!disabled && (
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    {enEdicion && <span className="cmms-ayuda" style={{ margin: 0, flex: 1 }}>Los cambios aplican sólo a este servicio; la rutina original no se modifica. Los pasos sin nombre no se guardan.</span>}
                    <ButtonItem type={enEdicion ? "primary" : "default"} size="small" htmlType="button" icon={enEdicion ? <CheckOutlined /> : <EditOutlined />} onClick={() => setEditando(!editando)}>
                        {enEdicion ? "Terminar de editar" : "Editar pasos"}
                    </ButtonItem>
                </div>
            )}
            {value.secciones.map((s, i) => {
                const pasos = s.actividades || [];
                const aplican = pasos.filter((a) => !a.noAplica);
                const total = aplican.length;
                const hechas = aplican.filter((a) => a.realizado).length;
                const noAplican = pasos.length - total;
                return (
                    <div key={i} style={{ marginBottom: 14 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#eef2f6", padding: "6px 10px", borderRadius: 6, marginBottom: 6 }}>
                            {enEdicion ? (
                                <>
                                    <Input size="small" value={s.titulo} placeholder="Nombre de la sección" onChange={(e) => setSeccion(i, { titulo: e.target.value })} style={{ flex: 1, fontWeight: 600 }} />
                                    <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => setSecciones(mover(value.secciones, i, -1))} />
                                    <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={i === value.secciones.length - 1} onClick={() => setSecciones(mover(value.secciones, i, 1))} />
                                    <Popconfirm title="¿Quitar toda la sección de este servicio?" okText="Quitar" cancelText="No" onConfirm={() => setSecciones(value.secciones.filter((_, j) => j !== i))}>
                                        <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                                    </Popconfirm>
                                </>
                            ) : (
                                <>
                                    <b style={{ flex: 1 }}>{`${i + 1}. ${s.titulo}`}</b>
                                    <Tag color={pasos.length && hechas === total ? "green" : "default"}>
                                        {hechas} de {total}
                                        {noAplican ? ` · ${noAplican} N/A` : ""}
                                    </Tag>
                                    {!disabled && total > 0 && (
                                        <ButtonItem type="default" size="small" htmlType="button" onClick={() => marcarTodo(i, hechas !== total)}>
                                            {hechas === total ? "Desmarcar todo" : "Marcar todo realizado"}
                                        </ButtonItem>
                                    )}
                                </>
                            )}
                        </div>

                        {enEdicion ? (
                            <>
                                {pasos.map((a, k) => (
                                    <div key={k} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 4px", borderBottom: "1px solid #f3f5f7" }}>
                                        <span style={{ color: "#7a8794", width: 34 }}>{`${i + 1}.${k + 1}`}</span>
                                        <Input
                                            size="small"
                                            autoFocus={!a.titulo}
                                            placeholder="Paso (ej. Inspección visual)"
                                            value={a.titulo}
                                            onChange={(e) => cambiarPaso(i, k, { titulo: e.target.value })}
                                            style={{ flex: "1 1 200px", fontWeight: 600 }}
                                        />
                                        <Input
                                            size="small"
                                            placeholder="Descripción (opcional)"
                                            value={a.descripcion}
                                            onChange={(e) => cambiarPaso(i, k, { descripcion: e.target.value })}
                                            style={{ flex: "2 1 260px" }}
                                        />
                                        <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={k === 0} onClick={() => setPasos(i, mover(pasos, k, -1))} />
                                        <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={k === pasos.length - 1} onClick={() => setPasos(i, mover(pasos, k, 1))} />
                                        <Tooltip title="Quitar paso">
                                            <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => setPasos(i, pasos.filter((_, n) => n !== k))} />
                                        </Tooltip>
                                    </div>
                                ))}
                                <Button type="link" size="small" icon={<PlusOutlined />} onClick={() => setPasos(i, [...pasos, pasoNuevo()])}>
                                    Agregar paso
                                </Button>
                            </>
                        ) : (
                            pasos.map((a, k) => (
                                <div key={k} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "5px 4px", borderBottom: "1px solid #f3f5f7" }}>
                                    <Checkbox disabled={disabled} checked={!!a.realizado} onChange={(e) => cambiarPaso(i, k, { realizado: e.target.checked, noAplica: false })} style={{ marginTop: 2 }} />
                                    <div style={{ flex: 1 }} className={a.noAplica ? "cmms-paso-na" : undefined}>
                                        <span style={{ color: "#7a8794", marginRight: 4 }}>{`${i + 1}.${k + 1}`}</span>
                                        <b>{a.titulo}</b> {a.descripcion}
                                        {(a.observacion || !disabled) && (
                                            <Input
                                                size="small"
                                                bordered={false}
                                                disabled={disabled}
                                                placeholder="Observación (opcional)"
                                                value={a.observacion}
                                                onChange={(e) => cambiarPaso(i, k, { observacion: e.target.value })}
                                                style={{ padding: 0, fontSize: 12, color: "#5b6b7c" }}
                                            />
                                        )}
                                    </div>
                                    {disabled ? (
                                        a.noAplica && <Tag style={{ marginTop: 2 }}>No aplica</Tag>
                                    ) : (
                                        <Tooltip title={a.noAplica ? "Volver a contar este paso" : "Este paso no aplica a este equipo (sale N/A en la Orden de Servicio)"}>
                                            <Tag.CheckableTag checked={!!a.noAplica} onChange={(noAplica) => cambiarPaso(i, k, { noAplica, realizado: false })} style={{ marginTop: 2, border: "1px solid #d9dee4" }}>
                                                No aplica
                                            </Tag.CheckableTag>
                                        </Tooltip>
                                    )}
                                </div>
                            ))
                        )}
                        {!enEdicion && !pasos.length && <div className="cmms-ayuda">Sección sin pasos. Use "Editar pasos" para agregarle.</div>}
                    </div>
                );
            })}
            {enEdicion && (
                <ButtonItem
                    type="dashed"
                    htmlType="button"
                    icon={<PlusOutlined />}
                    onClick={() => setSecciones([...value.secciones, { titulo: "Actividades", tipo: "checklist", actividades: [pasoNuevo()], observacionesGrupo: [] }])}
                >
                    Agregar sección
                </ButtonItem>
            )}
        </div>
    );
};

export default RutinaServicio;
